import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { SiteMeta } from '@/components/site/SiteMeta';
import { isValidEmail } from '@/lib/form-validation';
import { maskPhone } from '@/lib/masks';

const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
    <path d="M5 12l5 5L20 6" />
  </svg>
);

const SECTORS = [
  'Clínicas & saúde',
  'Imobiliárias',
  'Oficinas & autopeças',
  'Serviços & campo',
  'Beleza & estética',
  'Educação & cursos',
  'Varejo & comércio',
  'Outro',
] as const;

function SectorSelect({
  id,
  name = 'setor',
  defaultValue = SECTORS[0],
}: {
  id: string;
  name?: string;
  defaultValue?: (typeof SECTORS)[number];
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<(typeof SECTORS)[number]>(defaultValue);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(0, SECTORS.indexOf(defaultValue)),
  );
  const [menuBox, setMenuBox] = useState<{ top: number; left: number; width: number } | null>(null);

  const syncMenuBox = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const approxMenuH = Math.min(320, SECTORS.length * 48 + 16);
    const spaceBelow = window.innerHeight - r.bottom - gap - 12;
    const openUp = spaceBelow < approxMenuH && r.top > spaceBelow + 40;
    const top = openUp
      ? Math.max(12, r.top - gap - Math.min(approxMenuH, r.top - 16))
      : r.bottom + gap;
    setMenuBox({ top, left: r.left, width: r.width });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuBox(null);
      return;
    }
    syncMenuBox();
    const onMove = () => syncMenuBox();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (sector: (typeof SECTORS)[number]) => {
    setValue(sector);
    setActiveIndex(SECTORS.indexOf(sector));
    setOpen(false);
  };

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        pick(SECTORS[activeIndex]);
      } else if (e.key === 'ArrowDown') {
        setActiveIndex((i) => (i >= SECTORS.length - 1 ? 0 : i + 1));
      }
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? SECTORS.length - 1 : i - 1));
    }
  };

  const menu =
    open && menuBox
      ? createPortal(
          <ul
            ref={menuRef}
            id={listId}
            className="field-select-menu"
            role="listbox"
            aria-labelledby={id}
            style={{
              top: menuBox.top,
              left: menuBox.left,
              width: menuBox.width,
            }}
          >
            {SECTORS.map((sector, i) => {
              const selected = sector === value;
              return (
                <li
                  key={sector}
                  role="option"
                  aria-selected={selected}
                  className={selected ? 'is-active' : undefined}
                  data-active={i === activeIndex ? 'true' : undefined}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => pick(sector)}
                >
                  <span>{sector}</span>
                  <svg
                    className="tick"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.6}
                    aria-hidden
                  >
                    <path d="M5 12l5 5L20 6" />
                  </svg>
                </li>
              );
            })}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div className={`field-select${open ? ' is-open' : ''}`} ref={rootRef}>
      <input type="hidden" name={name} value={value} />
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="field-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
      >
        <span>{value}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {menu}
    </div>
  );
}

function Channel({ icon, title, desc }: { icon: ReactNode; title: string; desc: string }) {
  return (
    <div className="cc">
      <span className="ci">{icon}</span>
      <div>
        <div className="ct">{title}</div>
        <div className="cd">{desc}</div>
      </div>
    </div>
  );
}

export function ContatoPage() {
  const [sent, setSent] = useState(false);
  const [tel, setTel] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);

  const emailMessage = 'Informe um e-mail válido (ex.: voce@empresa.com.br).';

  const checkEmail = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return 'Informe seu e-mail.';
    if (!isValidEmail(trimmed)) return emailMessage;
    return null;
  };

  return (
    <>
      <SiteMeta
        title="Contato - Operax"
        description="Fale com a Operax: WhatsApp (11) 95502-2990, contato@operax.digital ou pelo formulário."
        path="/contato"
      />

      <section className="page-hero">
        <div className="hero-grid-bg" />
        <div className="shell page-hero-in rev">
          <h1>Fala com a gente.</h1>
          <p className="lead">
            Dúvida rápida? Chat do site ou WhatsApp. Formulário e time humano, no horário comercial.
          </p>
        </div>
      </section>

      <section className="pad" style={{ paddingTop: 8 }}>
        <div className="shell">
          <div className="contact-grid">
            <form
              className="form-card rev"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                setEmailTouched(true);
                const err = checkEmail(email);
                setEmailError(err);
                if (err) {
                  document.getElementById('email')?.focus();
                  return;
                }
                setSent(true);
              }}
            >
              <div className="field">
                <label htmlFor="nome">Nome</label>
                <input id="nome" name="nome" type="text" placeholder="Seu nome" />
              </div>
              <div className="field">
                <label htmlFor="empresa">Empresa</label>
                <input id="empresa" name="empresa" type="text" placeholder="Nome da empresa" />
              </div>
              <div className="contact-form-row">
                <div className="field">
                  <label htmlFor="email">E-mail</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="voce@empresa.com.br"
                    value={email}
                    aria-invalid={emailTouched && !!emailError}
                    aria-describedby={emailTouched && emailError ? 'email-error' : undefined}
                    className={emailTouched && emailError ? 'is-invalid' : undefined}
                    onChange={(e) => {
                      const next = e.target.value;
                      setEmail(next);
                      setSent(false);
                      if (emailTouched) setEmailError(checkEmail(next));
                    }}
                    onBlur={() => {
                      setEmailTouched(true);
                      setEmailError(checkEmail(email));
                    }}
                  />
                  {emailTouched && emailError ? (
                    <span id="email-error" className="field-error" role="alert">
                      {emailError}
                    </span>
                  ) : null}
                </div>
                <div className="field">
                  <label htmlFor="tel">WhatsApp</label>
                  <input
                    id="tel"
                    name="tel"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="(11) 90000-0000"
                    maxLength={15}
                    value={tel}
                    onChange={(e) => setTel(maskPhone(e.target.value))}
                  />
                </div>
              </div>
              <div className="field">
                <label htmlFor="setor">Setor</label>
                <SectorSelect id="setor" />
              </div>
              <div className="field">
                <label htmlFor="msg">Como podemos ajudar?</label>
                <textarea id="msg" name="msg" placeholder="Conte um pouco sobre a sua operação…" />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                Enviar mensagem <Arrow />
              </button>
              {sent && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: 16,
                    color: 'var(--ok)',
                    fontWeight: 700,
                    fontSize: '.9rem',
                  }}
                >
                  <span className="chk" style={{ background: 'rgba(18,183,106,.12)' }}>
                    <Check />
                  </span>
                  Recebemos. A equipe responde em horário comercial.
                </div>
              )}
            </form>

            <div className="contact-side rev" style={{ transitionDelay: '.06s' }}>
              <Channel
                icon={
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm-8 11-6-4v-1l6 4 6-4v1z" />
                  </svg>
                }
                title="WhatsApp"
                desc="(11) 95502-2990"
              />
              <Channel
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="m3 7 9 6 9-6" />
                  </svg>
                }
                title="E-mail"
                desc="contato@operax.digital"
              />
              <Channel
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                }
                title="Horário"
                desc="Segunda a sexta, 9h às 18h (Brasília)"
              />
              <Channel
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M12 22s8-6 8-12a8 8 0 1 0-16 0c0 6 8 12 8 12z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                }
                title="Endereço"
                desc="Rua Goiabal, 81, Apto 201, Centro Sul, Timóteo/MG, CEP 35180-970"
              />
              <div className="badge badge-wa" style={{ marginTop: 22 }}>
                <span className="meta-dot">
                  <svg viewBox="0 0 24 24" fill="#fff" style={{ width: 10, height: 10 }}>
                    <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm-8 11-6-4v-1l6 4 6-4v1z" />
                  </svg>
                </span>
                WhatsApp oficial da Meta · Provedor de Tecnologia
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
